import type { ReactNode } from 'react';
import { Container, Alert, AlertTitle } from '@mui/material';
import useAuth from '../hooks/useAuth';
import useLocales from '../hooks/useLocales';

type RoleBasedGuardProp = {
  accessibleRoles: (string | number)[];
  children: ReactNode | string;
};

const useCurrentRole = (): string | number | null => {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  // Check for registeredRole (number) - primary role identifier
  if (user.registeredRole !== undefined && user.registeredRole !== null) {
    return user.registeredRole;
  }

  // Fallback: check for role (string) if registeredRole is not available
  if (user.role) {
    return user.role;
  }

  return null;
};

export default function RoleBasedGuard({ accessibleRoles, children }: RoleBasedGuardProp) {
  const currentRole = useCurrentRole();
  const { translate } = useLocales();

  if (currentRole === null || !accessibleRoles.includes(currentRole)) {
    return (
      <Container sx={{ pt: 4 }}>
        <Alert severity="error">
          <AlertTitle>{translate('role_based_guard.permission_denied')}</AlertTitle>
          {translate('role_based_guard.you_do_not_have_permission_to_access_this_page')}
        </Alert>
      </Container>
    );
  }

  return <>{children}</>;
}
